import React, { useEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchSchools } from '../../redux/slices/School';
import { SnackbarSeverityType } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import SchoolOfficeUsersTable from './SchoolOfficeUsersTable';
import AddSchoolOfficeUserDialog from './AddSchoolOfficeUserDialog';
import { RootContainer, HeaderBar, AddUserButtonWrapper } from './styled';
import EditSchoolOfficeUserDialog from './EditSchoolOfficeUserDialog';
import { SchoolOfficeUser } from '../../types/schoolCollection';
import { useToast } from './utils/useToast';
import SchoolFilterBar from './components/SchoolFilterBar';
import userApi from '../../apis/users';
import Icons from '../../components/icons';

const AdminUsers: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { schools } = useSelector((state: RootState) => state.school);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [school, setSchool] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [users, setUsers] = useState<SchoolOfficeUser[]>([]);
  const [openAdd, setOpenAdd] = useState(false);
  const { toast, showToast, handleCloseToast } = useToast();
  const [editingUser, setEditingUser] = useState<SchoolOfficeUser | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    dispatch(fetchSchools());
  }, [dispatch]);

  const handleSearch = async () => {
    if (!selectedSchool) return;
    try {
      setIsSearching(true);
      const { data } = (await userApi.getUsers(selectedSchool, false)) as any;
      setUsers(data || []);
      setSchool(selectedSchool);
    } catch (e) {
      console.error(e);
      showToast('Failed to fetch users', SnackbarSeverityType.error);
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  const handleAddUser = (user: SchoolOfficeUser) => {
    setUsers((prev) => [...prev, user]);
  };

  const openEdit = (user: SchoolOfficeUser) => {
    setEditingUser(user);
  };

  const handleUpdateFromDialog = (updated: SchoolOfficeUser) => {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
  };

  const handleSelectedSchoolChange = (newSchool: string) => {
    setSelectedSchool(newSchool);
    if (newSchool !== school) {
      setUsers([]);
      setSchool('');
      setHasSearched(false);
    }
  };

  const isAddDisabled = !school || school !== selectedSchool;

  return (
    <RootContainer>
      <HeaderBar>
        <Typography variant='h5' fontWeight={600}>
          Admin Users
        </Typography>
        <AddUserButtonWrapper>
          <Button
            variant='contained'
            onClick={() => setOpenAdd(true)}
            disabled={isAddDisabled}
          >
            <Icons.PlusWhite style={{ marginRight: '6px' }} /> 
            Add User
          </Button>
        </AddUserButtonWrapper>
      </HeaderBar>
      <SchoolFilterBar
        schools={schools}
        selectedSchool={selectedSchool}
        onSelectedSchoolChange={handleSelectedSchoolChange}
        onSearch={handleSearch}
        isSearching={isSearching}
      />
      {hasSearched && (
        <SchoolOfficeUsersTable
          users={users}
          onEdit={openEdit}
          loading={isSearching}
        />
      )}
      <AddSchoolOfficeUserDialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        school={school}
        onAdd={handleAddUser}
        showToast={showToast}
      />
      <EditSchoolOfficeUserDialog
        user={editingUser}
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        schoolCode={school}
        onUpdate={handleUpdateFromDialog}
        showToast={showToast}
      />
      <ToastMessage
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </RootContainer>
  );
};

export default AdminUsers;
